<template>
    <v-container fluid>
        <v-form
            ref="form"
            v-model="validForm"
            lazy-validation
        >
            <v-row>
                <v-col
                    style="display: flex; align-content: center; align-items: center;"
                    cols="12"
                >
                    <h3>Equipment Warranty</h3>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <hr />
                </v-col>
            </v-row>

            <!-- Project (optional) -->
            <v-row>
                <v-col cols="12" lg="6" md="6" sm="12">
                    <label>Project (optional)</label>
                    <v-combobox
                        v-model="request.projectSelected"
                        :items="listProjects"
                        item-text="description"
                        item-value="id"
                        clearable
                        single-line
                        outlined
                        dense
                        @change="onProjectChange"
                    ></v-combobox>
                </v-col>
                
                <v-col cols="12" lg="6" md="6" sm="12">
                    <label>Customer *</label>
                    <v-combobox
                        v-model="request.customerSelected"
                        :items="listCustomer"
                        item-text="fullName"
                        item-value="id"
                        :rules="[validations.required]"
                        :disabled="projectCustomerLocked"
                        clearable
                        single-line
                        outlined
                        dense
                    ></v-combobox>
                </v-col>
                
                <v-col cols="12" lg="6" md="6" sm="12">
                    <label>Product *</label>
                    <v-combobox
                        v-model="request.productSelected"
                        :items="listProduct"
                        item-text="description"
                        item-value="id"
                        :rules="[validations.required]"
                        clearable
                        single-line
                        outlined
                        dense
                    ></v-combobox>
                </v-col>
                
                <v-col cols="12" lg="3" md="4" sm="12">
                    <label>Install Date *</label>
                    <v-menu
                        v-model="menuInstallDate"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                v-model="installDateFormatted"
                                v-bind="attrs"
                                v-on="on"
                                :rules="[validations.required]"
                                prepend-inner-icon="mdi-calendar"
                                readonly
                                single-line
                                outlined
                                dense
                            ></v-text-field>
                        </template>
                        <v-date-picker
                            v-model="request.installDate"
                            @input="menuInstallDate = false"
                        ></v-date-picker>
                    </v-menu>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <hr />
                </v-col>
            </v-row>

            <ActionButtons
                :request="request"
                :loading="saving"
                :showButtons="{
                    cancel: true,
                    save: true
                }"
                @cancel="cancel"
                @save="save"
            />
        </v-form>
    </v-container>
</template>

<script>
    import { required } from "@/utilities/Rules";
    import Helpers from '@/utilities/Helpers';
    import ActionButtons from "@/components/Layout/ActionButtons";

    export default ({

        mixins: [Helpers],

        components: {
            ActionButtons,
        },

        data: () => ({
            validForm: true,
            saving: false,
            menuInstallDate: false,
            projectCustomerLocked: false,

            listProjects: [],
            listCustomer: [],
            listProduct: [],
            listProductAll: [],

            request: {
                id: 0,
                projectSelected: null,
                customerSelected: null,
                productSelected: null,
                installDate: null,
                idServiceItemRef: null,
            },

            validations: {
                required: required,
            },
        }),

        props: {
            id: {
                default: 0
            }
        },

        computed: {
            installDateFormatted() {
                if (!this.request.installDate) return '';
                const [year, month, day] = this.request.installDate.split('-');
                return `${month}/${day}/${year}`;
            }
        },

        methods: {
            async onProjectChange(project) {
                if (!project || typeof project !== 'object') {
                    // No project selected - restore full customer and product lists
                    this.listProduct = [...this.listProductAll];
                    this.projectCustomerLocked = false;
                    return;
                }

                // Lock customer field and auto-select from list
                this.projectCustomerLocked = false;
                const customerMatch = this.listCustomer.find(c =>
                    c.fullName && project.customerName &&
                    c.fullName.toLowerCase() === project.customerName.toLowerCase()
                );
                if (customerMatch) {
                    this.request.customerSelected = customerMatch;
                    this.projectCustomerLocked = true;
                }

                // Load products from project items
                const items = await this.$store.dispatch("serviceModule/ListItems", project.id);
                if (items && Array.isArray(items)) {
                    const seen = new Set();
                    this.listProduct = items
                        .filter(item => {
                            if (!item.idProduct || seen.has(item.id)) return false;
                            seen.add(item.id);
                            return true;
                        })
                        .map(item => ({
                            id: item.id,           // service item ID (idserviceitem or idserviceitemcustom)
                            idProduct: item.idProduct, // real product FK
                            description: `${item.productCategoryDescription} -> ${item.description}`,
                        }));
                }
            },

            async getRegister() {
                if (this.id && parseInt(this.id) !== 0) {
                    const warranty = await this.$store.dispatch("equipmentWarrantyModule/GetById", this.id);

                    if (warranty) {
                        const project = warranty.idServiceProject
                            ? this.listProjects.find(p => p.id === warranty.idServiceProject) || null
                            : null;

                        // If a project was selected, rebuild the project product list first
                        if (project) {
                            await this.onProjectChange(project);
                        }

                        const customer = this.listCustomer.find(c => c.id === warranty.idCustomer) || null;

                        // Find product: prefer matching by service item ref in project list, fallback to product list
                        let product = null;
                        if (warranty.idServiceItemRef) {
                            product = this.listProduct.find(p => p.id === warranty.idServiceItemRef) || null;
                        }
                        if (!product) {
                            product = this.listProductAll.find(p => p.id === warranty.idProduct)
                                || { id: warranty.idProduct, description: warranty.productDescription };
                        }

                        this.request = {
                            id: warranty.id,
                            projectSelected: project,
                            customerSelected: customer,
                            productSelected: product,
                            installDate: warranty.installDate ? warranty.installDate.substring(0, 10) : null,
                            idServiceItemRef: warranty.idServiceItemRef || null,
                        };

                        // Restore customer lock when project is set
                        if (project && customer) {
                            this.projectCustomerLocked = true;
                        }
                    }
                }
            },

            async getLists() {
                this.listProjects = await this.$store.dispatch("serviceModule/ListProjectCompact", "-");
                this.listCustomer = await this.$store.dispatch("customerModule/List");
                this.listProductAll = await this.$store.dispatch("productModule/List");
                this.listProduct = [...this.listProductAll];
            },

            async cancel() {
                this.$router.push({ path: "/service/equipmentWarranty" });
            },

            async save() {
                await this.$refs.form.validate();

                if (this.validForm === false) {
                    this.showToast("error", "Warning!", "There are inconsistencies in the form. Please review!");
                    return;
                }

                if (!this.request.customerSelected || !this.request.customerSelected.id) {
                    this.showToast("error", "Warning!", "Customer is required.");
                    return;
                }

                if (!this.request.productSelected || !this.request.productSelected.id) {
                    this.showToast("error", "Warning!", "Product is required.");
                    return;
                }

                if (!this.request.installDate) {
                    this.showToast("error", "Warning!", "Install Date is required.");
                    return;
                }

                this.saving = true;

                const saveRequest = {
                    id: this.request.id,
                    // If product came from a project item it has idProduct (the real FK); otherwise id is the product FK
                    idProduct: this.request.productSelected.idProduct !== undefined
                        ? this.request.productSelected.idProduct
                        : this.request.productSelected.id,
                    idServiceProject: this.request.projectSelected && this.request.projectSelected.id
                        ? this.request.projectSelected.id
                        : null,
                    // Store the service item reference when product came from a project
                    idServiceItemRef: this.request.productSelected.idProduct !== undefined
                        ? this.request.productSelected.id
                        : null,
                    idCustomer: this.request.customerSelected.id,
                    installDate: this.request.installDate,
                };

                const result = await this.$store.dispatch("equipmentWarrantyModule/CreateUpdate", saveRequest);

                if (result && result.success !== false) {
                    this.showToast("success", "Success!", "Equipment warranty saved successfully.");
                    this.$router.push({ path: "/sales/equipmentWarranty" });
                } else {
                    this.showToast("error", "Error!", result ? result.message : "Error saving record.");
                }

                this.saving = false;
            },
        },

        async created() {
            this.showLoading();
            await this.getLists();
            await this.getRegister();
            this.hideLoading();
        }
    })
</script>
